import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { MINDEF_RESPONSE_TYPE } from "../../app-admin/auth/constants/mindef";
import AuthService from "../../app-admin/users/services/auth.service";
import UserService from "../../app-admin/users/services/user.service";

const AuthContext = React.createContext({
    isAuth: false,
    isLoaded: false,
    isDisabled: false,
    isUnknown: false,
    user: null,
    login: () => null,
    logout: () => null
});
const AuthConsumer = AuthContext.Consumer;

function AuthProvider({ children }) {
    const [authState, setAuthState] = useState({
        isAuth: false,
        isLoaded: false,
        isDisabled: false,
        isUnknown: false,
        user: null
    });

    const history = useHistory();

    useEffect(() => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const [code, state, expired] = [urlParams.get(MINDEF_RESPONSE_TYPE), urlParams.get("session_state"), urlParams.get("expired")];
        if (code && state) {
            AuthService.login(code)
                .then(() => UserService.getCurrent())
                .then(user => {
                    login(user);
                    history.push("/");
                })
                .catch(error => handleUnknownAndDisabled(error));
        } else if (expired) {
            logout();
        } else {
            UserService.getCurrent()
                .then(user => login(user))
                .catch(error => handleUnknownAndDisabled(error));
        }
    }, []);

    const login = user => {
        if (user) {
            setAuthState({
                isAuth: true,
                isLoaded: true,
                isDisabled: false,
                isUnknown: false,
                user
            });
        }
    };

    const handleUnknownAndDisabled = error => {
        if (error.response.data.message === "ACCOUNT_DISABLED") {
            setAuthState({
                isAuth: false,
                isLoaded: true,
                isDisabled: true,
                isUnknown: false,
                user: null
            });
        } else if (error.response.data.message === "ACCOUNT_UNKNOWN") {
            setAuthState({
                isAuth: false,
                isLoaded: true,
                isDisabled: false,
                isUnknown: true,
                user: null
            });
        }
        history.push("/login");
    };

    const logout = () => {
        setAuthState({
            isAuth: false,
            isLoaded: true,
            isDisabled: false,
            isUnknown: false,
            user: null
        });
    };

    const contextValue = useMemo(
        () => ({
            isAuth: authState.isAuth,
            isLoaded: authState.isLoaded,
            isDisabled: authState.isDisabled,
            isUnknown: authState.isUnknown,
            user: authState.user,
            logout
        }),
        [authState.isAuth, authState.isLoaded, authState.user, authState.isDisabled, authState.isUnknown]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export { AuthConsumer, AuthContext, AuthProvider };

